/**
 * SequentialObserver:
 * 1. Observe single element;
 * 2. Allow executing multiple callbacks at different threshold points.
 *
 * @param {string} selector
 * @param {ThresholdGroup[]} thresholdGroups
 * @param {Object} observerConfig - native IntersectionObserver config options
 *
 * @typedef {Object} ThresholdGroup
 * @property {number} threshold - (0-1)
 * @property {string} [condition=">="] - '>=', '<=', '>', '<', '=='
 * @property {Function[]} callbacks - sequential callback functions
 * @property {boolean} [triggerOnce=true]
 */

function createSequentialObserver({ selector, thresholdGroups = [], observerConfig = {} }) {
  const element = document.querySelector(selector);
  if (!element) return;

  function normalizeTasks(groups) {
    let order = 0;

    return groups.flatMap((group, groupIndex) =>
      group.callbacks.map((callback, callbackIndex) => ({
        id: `g${groupIndex}_c${callbackIndex}`,
        threshold: group.threshold,
        condition: group.condition || ">=",
        callback,
        triggerOnce: group.triggerOnce !== false,
        triggered: false,
        order: order++,
      })),
    );
  }

  function getUniqueThresholds(groups) {
    return Array.from(new Set(groups.map((g) => g.threshold))).sort((a, b) => a - b);
  }

  function check(condition, ratio, threshold) {
    const operators = {
      ">=": (r, t) => r >= t,
      "<=": (r, t) => r <= t,
      ">": (r, t) => r > t,
      "<": (r, t) => r < t,
      "==": (r, t) => Math.abs(r - t) < 0.0001,
    };

    return (operators[condition] || operators[">="])(ratio, threshold);
  }

  function shouldRun(task, current, previous) {
    if (task.triggerOnce && task.triggered) return false;

    const now = check(task.condition, current, task.threshold);
    const before = check(task.condition, previous, task.threshold);

    return now && !before;
  }

  function runTask(task, entry) {
    try {
      task.callback(entry);
      if (task.triggerOnce) task.triggered = true;
    } catch (err) {
      console.error(`[SequentialObserver] Error in ${task.id}`, err);
    }
  }

  // Core
  const tasks = normalizeTasks(thresholdGroups);
  const thresholds = getUniqueThresholds(tasks);

  let lastRatio = -Infinity;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const ratio = entry.intersectionRatio;

        const readyTasks = tasks.filter((task) => shouldRun(task, ratio, lastRatio)).sort((a, b) => a.order - b.order);

        readyTasks.forEach((task) => runTask(task, entry));

        lastRatio = ratio;
      });
    },
    Object.assign({}, observerConfig, { threshold: thresholds }),
  );

  observer.observe(element);
}

module.exports = createSequentialObserver;
